// packages/shared/src/constants/portfolio.constants.ts
// Single source of truth for portfolio domain value-sets (ADR-20, section 5).

import type { ChipVariant } from "../types/smart-table";

export const PORTFOLIO_MIN_SUPPORTED_YEAR = 1900;
export const PORTFOLIO_MAX_SUPPORTED_YEAR = 2100;

export const PORTFOLIO_POSITION_STATUS = ["open", "closed", "all"] as const;
export type TPortfolioPositionStatus =
  (typeof PORTFOLIO_POSITION_STATUS)[number];
export const PORTFOLIO_POSITION_STATUS_OBJ = {
  OPEN: "open",
  CLOSED: "closed",
  ALL: "all",
} as const satisfies Record<string, TPortfolioPositionStatus>;

export const PORTFOLIO_UNKNOWN_ACCOUNT_ID = "unknown";

export const PORTFOLIO_ACCOUNT_FILTER_OBJ = {
  ALL: "all",
} as const;

// ── displayCurrency ──────────────────────────────────────────────────────────
// Currencies available for portfolio display. SSOT for the CurrencySwitcher and
// for the `portfolios.display_currency` column default.
export const DISPLAY_CURRENCY = ["USD", "RUB", "EUR", "CNH"] as const;
export type TDisplayCurrency = (typeof DISPLAY_CURRENCY)[number];
export const DISPLAY_CURRENCY_OBJ = {
  USD: "USD",
  RUB: "RUB",
  EUR: "EUR",
  CNH: "CNH",
} as const satisfies Record<string, TDisplayCurrency>;
export const DISPLAY_CURRENCY_LABELS: Record<
  TDisplayCurrency,
  Record<string, string>
> = {
  USD: { "en-US": "USD", "ru-RU": "USD" },
  RUB: { "en-US": "RUB", "ru-RU": "RUB" },
  EUR: { "en-US": "EUR", "ru-RU": "EUR" },
  CNH: { "en-US": "CNH", "ru-RU": "CNH" },
};
export const DISPLAY_CURRENCY_SYMBOL: Record<TDisplayCurrency, string> = {
  USD: "$",
  RUB: "₽",
  EUR: "€",
  CNH: "¥",
};

// ── accountType ─────────────────────────────────────────────────────────────
export const ACCOUNT_TYPE = ["iis", "standard", "ib", "other"] as const;
export type TAccountType = (typeof ACCOUNT_TYPE)[number];
export const ACCOUNT_TYPE_OBJ = {
  IIS: "iis",
  STANDARD: "standard",
  IB: "ib",
  OTHER: "other",
} as const satisfies Record<string, TAccountType>;
export const ACCOUNT_TYPE_LABELS: Record<
  TAccountType,
  Record<string, string>
> = {
  iis: { "en-US": "IIS", "ru-RU": "ИИС" },
  standard: { "en-US": "Standard", "ru-RU": "Обычный" },
  ib: { "en-US": "Interactive Brokers", "ru-RU": "IB" },
  other: { "en-US": "Other", "ru-RU": "Другой" },
};
export const ACCOUNT_TYPE_VARIANTS: Record<TAccountType, ChipVariant> = {
  iis: "default",
  standard: "secondary",
  ib: "outline",
  other: "secondary",
};

// ── accountStatus ───────────────────────────────────────────────────────────
// Derived from `closedOn` on a cutoff date; never stored as a column.
export const PORTFOLIO_ACCOUNT_STATUS = ["open", "closed"] as const;
export type TPortfolioAccountStatus =
  (typeof PORTFOLIO_ACCOUNT_STATUS)[number];
export const PORTFOLIO_ACCOUNT_STATUS_OBJ = {
  OPEN: "open",
  CLOSED: "closed",
} as const satisfies Record<string, TPortfolioAccountStatus>;
export const PORTFOLIO_ACCOUNT_STATUS_LABELS: Record<
  TPortfolioAccountStatus,
  Record<string, string>
> = {
  open: { "en-US": "Open", "ru-RU": "Действующий" },
  closed: { "en-US": "Closed", "ru-RU": "Закрытый" },
};
export const PORTFOLIO_ACCOUNT_STATUS_VARIANTS: Record<
  TPortfolioAccountStatus,
  ChipVariant
> = {
  open: "default",
  closed: "secondary",
};

export const PORTFOLIO_ACCOUNT_STATUS_FILTER = [
  ...PORTFOLIO_ACCOUNT_STATUS,
  "all",
] as const;
export type TPortfolioAccountStatusFilter =
  (typeof PORTFOLIO_ACCOUNT_STATUS_FILTER)[number];
export const PORTFOLIO_ACCOUNT_STATUS_FILTER_OBJ = {
  ...PORTFOLIO_ACCOUNT_STATUS_OBJ,
  ALL: "all",
} as const satisfies Record<string, TPortfolioAccountStatusFilter>;
export const PORTFOLIO_ACCOUNT_STATUS_FILTER_LABELS: Record<
  TPortfolioAccountStatusFilter,
  Record<string, string>
> = {
  open: { "en-US": "Open", "ru-RU": "Действующие" },
  closed: { "en-US": "Closed", "ru-RU": "Закрытые" },
  all: { "en-US": "All", "ru-RU": "Все" },
};
/** Catalog filter used when the caller does not pass one (API compatibility). */
export const PORTFOLIO_ACCOUNT_STATUS_FILTER_DEFAULT: TPortfolioAccountStatusFilter =
  PORTFOLIO_ACCOUNT_STATUS_FILTER_OBJ.ALL;
/** Initial filter of the Accounts tab. */
export const PORTFOLIO_ACCOUNT_STATUS_FILTER_UI_DEFAULT: TPortfolioAccountStatusFilter =
  PORTFOLIO_ACCOUNT_STATUS_FILTER_OBJ.OPEN;

// Technical zero tolerances (account closure and allocation emptiness).
export const PORTFOLIO_MONEY_ZERO_TOLERANCE = 1e-7;
export const PORTFOLIO_QUANTITY_ZERO_TOLERANCE = 1e-9;

// ── securityType ────────────────────────────────────────────────────────────
export const SECURITY_TYPE = [
  "stock",
  "preferred_stock",
  "adr",
  "bond",
  "fund",
  "currency",
  "warrant",
] as const;
export type TSecurityType = (typeof SECURITY_TYPE)[number];
export const SECURITY_TYPE_OBJ = {
  STOCK: "stock",
  PREFERRED_STOCK: "preferred_stock",
  ADR: "adr",
  BOND: "bond",
  FUND: "fund",
  CURRENCY: "currency",
  WARRANT: "warrant",
} as const satisfies Record<string, TSecurityType>;
export const SECURITY_TYPE_LABELS: Record<
  TSecurityType,
  Record<string, string>
> = {
  stock: { "en-US": "Stock", "ru-RU": "Акция" },
  preferred_stock: { "en-US": "Preferred stock", "ru-RU": "Прив. акция" },
  adr: { "en-US": "ADR/GDR", "ru-RU": "АДР/ГДР" },
  bond: { "en-US": "Bond", "ru-RU": "Облигация" },
  fund: { "en-US": "Fund", "ru-RU": "Фонд" },
  currency: { "en-US": "Currency", "ru-RU": "Валюта" },
  warrant: { "en-US": "Warrant", "ru-RU": "Варрант" },
};
export const SECURITY_TYPE_VARIANTS: Record<TSecurityType, ChipVariant> = {
  stock: "default",
  preferred_stock: "secondary",
  adr: "outline",
  bond: "secondary",
  fund: "outline",
  currency: "default",
  warrant: "outline",
};

// ── transactionType ─────────────────────────────────────────────────────────
export const TRANSACTION_TYPE = ["buy", "sell"] as const;
export type TTransactionType = (typeof TRANSACTION_TYPE)[number];
export const TRANSACTION_TYPE_OBJ = {
  BUY: "buy",
  SELL: "sell",
} as const satisfies Record<string, TTransactionType>;
export const TRANSACTION_TYPE_LABELS: Record<
  TTransactionType,
  Record<string, string>
> = {
  buy: { "en-US": "Buy", "ru-RU": "Покупка" },
  sell: { "en-US": "Sell", "ru-RU": "Продажа" },
};
export const TRANSACTION_TYPE_VARIANTS: Record<TTransactionType, ChipVariant> =
  {
    buy: "default",
    sell: "secondary",
  };

// ── corporateAction ─────────────────────────────────────────────────────────
export const CORPORATE_ACTION_TYPE = [
  "split",
  "conversion",
  "write_off",
  "grant",
  "depository_movement",
  "issue_exchange",
  "warrant_receipt",
  "warrant_basis_confirmation",
  "warrant_expiration",
] as const;
export type TCorporateActionType = (typeof CORPORATE_ACTION_TYPE)[number];
export const CORPORATE_ACTION_TYPE_OBJ = {
  SPLIT: "split",
  CONVERSION: "conversion",
  WRITE_OFF: "write_off",
  GRANT: "grant",
  DEPOSITORY_MOVEMENT: "depository_movement",
  ISSUE_EXCHANGE: "issue_exchange",
  WARRANT_RECEIPT: "warrant_receipt",
  WARRANT_BASIS_CONFIRMATION: "warrant_basis_confirmation",
  WARRANT_EXPIRATION: "warrant_expiration",
} as const satisfies Record<string, TCorporateActionType>;
export const CORPORATE_ACTION_TYPE_LABELS: Record<
  TCorporateActionType,
  Record<string, string>
> = {
  split: { "en-US": "Split", "ru-RU": "Дробление" },
  conversion: { "en-US": "Conversion", "ru-RU": "Конвертация" },
  write_off: { "en-US": "Write-off", "ru-RU": "Списание" },
  grant: { "en-US": "Grant", "ru-RU": "Зачисление" },
  depository_movement: { "en-US": "Confirmed custody movement", "ru-RU": "Подтверждённое перемещение бумаг" },
  issue_exchange: { "en-US": "Confirmed issue exchange", "ru-RU": "Подтверждённая замена выпуска" },
  warrant_receipt: { "en-US": "Warrant receipt", "ru-RU": "Получение варрантов" },
  warrant_basis_confirmation: { "en-US": "Confirmed warrant cost basis", "ru-RU": "Подтверждение себестоимости варрантов" },
  warrant_expiration: { "en-US": "Confirmed warrant expiration", "ru-RU": "Подтверждённое истечение варрантов" },
};
export const CORPORATE_ACTION_TYPE_VARIANTS: Record<
  TCorporateActionType,
  ChipVariant
> = {
  split: "secondary",
  conversion: "outline",
  write_off: "destructive",
  grant: "emerald",
  depository_movement: "secondary",
  issue_exchange: "outline",
  warrant_receipt: "emerald",
  warrant_basis_confirmation: "outline",
  warrant_expiration: "destructive",
};

/** A missing warrant basis is never an assertion that the receipt was free. */
export const PORTFOLIO_WARRANT_BASIS_KIND = ["unknown", "verified-zero", "source-allocation"] as const;
export type TPortfolioWarrantBasisKind = (typeof PORTFOLIO_WARRANT_BASIS_KIND)[number];
export const PORTFOLIO_WARRANT_BASIS_KIND_OBJ = {
  UNKNOWN: "unknown", VERIFIED_ZERO: "verified-zero", SOURCE_ALLOCATION: "source-allocation",
} as const satisfies Record<string, TPortfolioWarrantBasisKind>;
export const PORTFOLIO_WARRANT_BASIS_KIND_LABELS: Record<TPortfolioWarrantBasisKind, Record<string, string>> = {
  unknown: { "en-US": "Unconfirmed cost basis", "ru-RU": "Себестоимость не подтверждена" },
  "verified-zero": { "en-US": "Confirmed zero cost basis", "ru-RU": "Подтверждённая нулевая себестоимость" },
  "source-allocation": { "en-US": "Confirmed allocation", "ru-RU": "Подтверждённое распределение стоимости" },
};
export const PORTFOLIO_WARRANT_BASIS_KIND_VARIANTS: Record<TPortfolioWarrantBasisKind, ChipVariant> = {
  unknown: "secondary", "verified-zero": "outline", "source-allocation": "outline",
};

/** Explicitly confirmed statement events; separate from automatic market events. */
export const PORTFOLIO_SECURITY_MOVEMENT_KIND = ["depository-movement", "own-account-transfer", "issue-exchange"] as const;
export type TPortfolioSecurityMovementKind = (typeof PORTFOLIO_SECURITY_MOVEMENT_KIND)[number];
export const PORTFOLIO_SECURITY_MOVEMENT_KIND_OBJ = {
  DEPOSITORY_MOVEMENT: "depository-movement",
  OWN_ACCOUNT_TRANSFER: "own-account-transfer",
  ISSUE_EXCHANGE: "issue-exchange",
} as const satisfies Record<string, TPortfolioSecurityMovementKind>;
export const PORTFOLIO_SECURITY_MOVEMENT_KIND_LABELS: Record<TPortfolioSecurityMovementKind, Record<string, string>> = {
  "depository-movement": { "en-US": "Custody movement", "ru-RU": "Депозитарное перемещение" },
  "own-account-transfer": { "en-US": "Transfer between own accounts", "ru-RU": "Перевод между своими счетами" },
  "issue-exchange": { "en-US": "Issue exchange", "ru-RU": "Замена выпуска" },
};
export const PORTFOLIO_SECURITY_MOVEMENT_KIND_VARIANTS: Record<TPortfolioSecurityMovementKind, ChipVariant> = {
  "depository-movement": "secondary", "own-account-transfer": "secondary", "issue-exchange": "outline",
};

export const PORTFOLIO_SECURITY_MOVEMENT_DOCUMENT_SOURCE = ["sec", "moex"] as const;
export type TPortfolioSecurityMovementDocumentSource = (typeof PORTFOLIO_SECURITY_MOVEMENT_DOCUMENT_SOURCE)[number];
export const PORTFOLIO_SECURITY_MOVEMENT_DOCUMENT_SOURCE_OBJ = {
  SEC: "sec", MOEX: "moex",
} as const satisfies Record<string, TPortfolioSecurityMovementDocumentSource>;
export const PORTFOLIO_SECURITY_MOVEMENT_DOCUMENT_SOURCE_LABELS: Record<TPortfolioSecurityMovementDocumentSource, Record<string, string>> = {
  sec: { "en-US": "SEC filing", "ru-RU": "Документ SEC" },
  moex: { "en-US": "Moscow Exchange document", "ru-RU": "Документ Московской биржи" },
};
export const PORTFOLIO_SECURITY_MOVEMENT_DOCUMENT_SOURCE_VARIANTS: Record<TPortfolioSecurityMovementDocumentSource, ChipVariant> = {
  sec: "outline", moex: "outline",
};
export const PORTFOLIO_SECURITY_MOVEMENT_DOCUMENT_HOSTS: Readonly<Record<TPortfolioSecurityMovementDocumentSource, readonly string[]>> = {
  sec: ["sec.gov", "www.sec.gov"], moex: ["moex.com", "www.moex.com"],
};

export const CORPORATE_ACTION_LEG_ROLE = ["out", "in"] as const;
export type TCorporateActionLegRole =
  (typeof CORPORATE_ACTION_LEG_ROLE)[number];
export const CORPORATE_ACTION_LEG_ROLE_OBJ = {
  OUT: "out",
  IN: "in",
} as const satisfies Record<string, TCorporateActionLegRole>;
export const CORPORATE_ACTION_LEG_ROLE_LABELS: Record<
  TCorporateActionLegRole,
  Record<string, string>
> = {
  out: { "en-US": "Out", "ru-RU": "Списание" },
  in: { "en-US": "In", "ru-RU": "Зачисление" },
};
export const CORPORATE_ACTION_LEG_ROLE_VARIANTS: Record<
  TCorporateActionLegRole,
  ChipVariant
> = {
  out: "secondary",
  in: "emerald",
};

// ── incomeEventType ─────────────────────────────────────────────────────────
// Income events track cash distributions from securities (dividends, coupons,
// withholding tax). Separated from transactions (buy/sell) per portfolio-income-events plan.
export const INCOME_EVENT_TYPE = [
  "dividend",
  "coupon",
  "withholding_tax",
] as const;
export type TIncomeEventType = (typeof INCOME_EVENT_TYPE)[number];
export const INCOME_EVENT_TYPE_OBJ = {
  DIVIDEND: "dividend",
  COUPON: "coupon",
  WITHHOLDING_TAX: "withholding_tax",
} as const satisfies Record<string, TIncomeEventType>;
export const INCOME_EVENT_IMPORT_TYPE = [
  INCOME_EVENT_TYPE_OBJ.DIVIDEND,
  INCOME_EVENT_TYPE_OBJ.COUPON,
] as const;
export type TIncomeEventImportType = (typeof INCOME_EVENT_IMPORT_TYPE)[number];
export function isIncomeEventImportType(
  value: unknown,
): value is TIncomeEventImportType {
  return INCOME_EVENT_IMPORT_TYPE.some((type) => type === value);
}
export const INCOME_EVENT_TYPE_LABELS: Record<
  TIncomeEventType,
  Record<string, string>
> = {
  dividend: { "en-US": "Dividend", "ru-RU": "Дивиденд" },
  coupon: { "en-US": "Coupon", "ru-RU": "Купон" },
  withholding_tax: { "en-US": "Withholding tax", "ru-RU": "Удержанный налог" },
};
export const INCOME_EVENT_TYPE_VARIANTS: Record<TIncomeEventType, ChipVariant> =
  {
    dividend: "outline",
    coupon: "secondary",
    withholding_tax: "outline",
  };

export const MONEY_SIGN = ["positive", "negative"] as const;
export type TMoneySign = (typeof MONEY_SIGN)[number];
export const MONEY_SIGN_OBJ = {
  POSITIVE: MONEY_SIGN[0],
  NEGATIVE: MONEY_SIGN[1],
} as const satisfies Record<string, TMoneySign>;

// Expected sign for each income event type: "positive" | "negative".
// SSOT for sign↔type validation (portfolio-income-events plan, Stage 1).
// dividend/coupon → money in (positive); withholding_tax → money out (negative).
export const INCOME_EVENT_TYPE_SIGN: Record<TIncomeEventType, TMoneySign> = {
  dividend: MONEY_SIGN_OBJ.POSITIVE,
  coupon: MONEY_SIGN_OBJ.POSITIVE,
  withholding_tax: MONEY_SIGN_OBJ.NEGATIVE,
};

// ── cashflowReason ──────────────────────────────────────────────────────────
// dividend only for paired CashFlow (auto-created from Transaction), manual input forbidden
export const CASHFLOW_REASON = [
  "deposit",
  "withdrawal",
  "commission",
  "tax",
  "transfer",
  "trade",
  "dividend",
  "other",
] as const;
export type TCashflowReason = (typeof CASHFLOW_REASON)[number];
export const CASHFLOW_REASON_OBJ = {
  DEPOSIT: "deposit",
  WITHDRAWAL: "withdrawal",
  COMMISSION: "commission",
  TAX: "tax",
  TRANSFER: "transfer",
  TRADE: "trade",
  DIVIDEND: "dividend",
  OTHER: "other",
} as const satisfies Record<string, TCashflowReason>;
export const CASHFLOW_REASON_LABELS: Record<
  TCashflowReason,
  Record<string, string>
> = {
  deposit: { "en-US": "Deposit", "ru-RU": "Пополнение" },
  withdrawal: { "en-US": "Withdrawal", "ru-RU": "Вывод" },
  commission: { "en-US": "Commission", "ru-RU": "Комиссия" },
  tax: { "en-US": "Tax", "ru-RU": "Налог" },
  transfer: { "en-US": "Transfer", "ru-RU": "Перевод" },
  trade: { "en-US": "Trade", "ru-RU": "Сделка" },
  dividend: { "en-US": "Dividend", "ru-RU": "Дивиденд" },
  other: { "en-US": "Other", "ru-RU": "Прочее" },
};
export const CASHFLOW_REASON_VARIANTS: Record<TCashflowReason, ChipVariant> = {
  deposit: "default",
  withdrawal: "secondary",
  commission: "outline",
  tax: "outline",
  transfer: "secondary",
  trade: "default",
  dividend: "outline",
  other: "secondary",
};
// Manual input allowed for these reasons (trade allowed for corrections, dividend forbidden)
export const CASHFLOW_REASON_MANUAL = [
  "deposit",
  "withdrawal",
  "commission",
  "tax",
  "transfer",
  "trade",
  "other",
] as const satisfies readonly TCashflowReason[];

// Expected sign for each CashFlow reason: "positive" | "negative" | "any".
// SSOT for sign↔reason validation (ADR-20, section 5).
// deposit → money in (positive); withdrawal → money out (negative);
// commission/tax/trade/transfer/other → either sign (commission/tax refunds are positive; corrections; transfers split into +/- pair).
export const CASHFLOW_REASON_SIGN: Record<TCashflowReason, TMoneySign | "any"> =
  {
    deposit: MONEY_SIGN_OBJ.POSITIVE,
    withdrawal: MONEY_SIGN_OBJ.NEGATIVE,
    commission: "any",
    tax: "any",
    transfer: "any",
    trade: "any",
    dividend: MONEY_SIGN_OBJ.POSITIVE,
    other: "any",
  };

// ── importSource ────────────────────────────────────────────────────────────
export const IMPORT_SOURCE = ["manual", "import", "broker"] as const;
export type TImportSource = (typeof IMPORT_SOURCE)[number];
export const IMPORT_SOURCE_OBJ = {
  MANUAL: "manual",
  IMPORT: "import",
  BROKER: "broker",
} as const satisfies Record<string, TImportSource>;
export const IMPORT_SOURCE_LABELS: Record<
  TImportSource,
  Record<string, string>
> = {
  manual: { "en-US": "Manual", "ru-RU": "Вручную" },
  import: { "en-US": "Import", "ru-RU": "Импорт" },
  broker: { "en-US": "Broker", "ru-RU": "Брокер" },
};
export const IMPORT_SOURCE_VARIANTS: Record<TImportSource, ChipVariant> = {
  manual: "default",
  import: "secondary",
  broker: "outline",
};

// ── portfolio blocked quantity event identity ─────────────────────────────
export const PORTFOLIO_BLOCKED_QUANTITY_IDENTITY_TYPE = [
  "transaction",
  "external",
] as const;
export type TPortfolioBlockedQuantityIdentityType =
  (typeof PORTFOLIO_BLOCKED_QUANTITY_IDENTITY_TYPE)[number];
export const PORTFOLIO_BLOCKED_QUANTITY_IDENTITY_TYPE_OBJ = {
  TRANSACTION: "transaction",
  EXTERNAL: "external",
} as const satisfies Record<string, TPortfolioBlockedQuantityIdentityType>;

// ── exchangeRateSource ──────────────────────────────────────────────────────
export const EXCHANGE_RATE_SOURCE = ["manual", "fmp"] as const;
export type TExchangeRateSource = (typeof EXCHANGE_RATE_SOURCE)[number];
export const EXCHANGE_RATE_SOURCE_OBJ = {
  MANUAL: "manual",
  FMP: "fmp",
} as const satisfies Record<string, TExchangeRateSource>;
export const EXCHANGE_RATE_SOURCE_LABELS: Record<
  TExchangeRateSource,
  Record<string, string>
> = {
  manual: { "en-US": "Manual", "ru-RU": "Вручную" },
  fmp: { "en-US": "FMP", "ru-RU": "FMP" },
};
export const EXCHANGE_RATE_SOURCE_VARIANTS: Record<
  TExchangeRateSource,
  ChipVariant
> = {
  manual: "default",
  fmp: "secondary",
};

// ── priceSource ──────────────────────────────────────────────────────────────
// Tracks the provider or user source responsible for the current symbol price.
// Automatic FMP sync must not overwrite manual or MOEX-managed prices.
export const PRICE_SOURCE = ["fmp", "manual", "moex"] as const;
export type TPriceSource = (typeof PRICE_SOURCE)[number];
export const PRICE_SOURCE_OBJ = {
  FMP: "fmp",
  MANUAL: "manual",
  MOEX: "moex",
} as const satisfies Record<string, TPriceSource>;

/** Exchange code of the Moscow Exchange; securities listed there are priced by MOEX, not FMP. */
export const MOEX_EXCHANGE_CODE = "MCX";
export function isMoexExchange(exchange: string | null | undefined): boolean {
  return exchange?.trim().toUpperCase() === MOEX_EXCHANGE_CODE;
}
/** The single rule for a new Symbol's price source: MCX is priced by MOEX, every other or unknown exchange by FMP. */
export function defaultPriceSourceForExchange(
  exchange: string | null | undefined,
): typeof PRICE_SOURCE_OBJ.MOEX | typeof PRICE_SOURCE_OBJ.FMP {
  return isMoexExchange(exchange) ? PRICE_SOURCE_OBJ.MOEX : PRICE_SOURCE_OBJ.FMP;
}
export const PRICE_SOURCE_LABELS: Record<
  TPriceSource,
  Record<string, string>
> = {
  fmp: { "en-US": "FMP", "ru-RU": "FMP" },
  manual: { "en-US": "Manual", "ru-RU": "Вручную" },
  moex: { "en-US": "MOEX", "ru-RU": "MOEX" },
};
export const PRICE_SOURCE_VARIANTS: Record<TPriceSource, ChipVariant> = {
  fmp: "secondary",
  manual: "default",
  moex: "outline",
};

// Provider identities are maintained only for automatic market-data sources.
export const MARKET_DATA_PROVIDER = [
  PRICE_SOURCE_OBJ.FMP,
  PRICE_SOURCE_OBJ.MOEX,
] as const;
export type TMarketDataProvider = (typeof MARKET_DATA_PROVIDER)[number];

// ── portfolioValuationStatus ────────────────────────────────────────────────
export const PORTFOLIO_VALUATION_STATUS = ["complete", "incomplete"] as const;
export type TPortfolioValuationStatus =
  (typeof PORTFOLIO_VALUATION_STATUS)[number];
export const PORTFOLIO_VALUATION_STATUS_OBJ = {
  COMPLETE: "complete",
  INCOMPLETE: "incomplete",
} as const satisfies Record<string, TPortfolioValuationStatus>;
export const PORTFOLIO_VALUATION_STATUS_LABELS: Record<
  TPortfolioValuationStatus,
  Record<string, string>
> = {
  complete: { "en-US": "Complete", "ru-RU": "Полная" },
  incomplete: { "en-US": "Incomplete", "ru-RU": "Неполная" },
};
export const PORTFOLIO_VALUATION_STATUS_VARIANTS: Record<
  TPortfolioValuationStatus,
  ChipVariant
> = {
  complete: "emerald",
  incomplete: "destructive",
};

// ── portfolio market-price decision ────────────────────────────────────────
export const PORTFOLIO_VALUATION_POLICY_VERSION = 2;

export const PORTFOLIO_PRICE_DECISION_STATE = [
  "fresh",
  "stale",
  "manual",
  "missing",
  "identity-conflict",
  "event-pending",
] as const;
export type TPortfolioPriceDecisionState =
  (typeof PORTFOLIO_PRICE_DECISION_STATE)[number];
export const PORTFOLIO_PRICE_DECISION_STATE_OBJ = {
  FRESH: "fresh",
  STALE: "stale",
  MANUAL: "manual",
  MISSING: "missing",
  IDENTITY_CONFLICT: "identity-conflict",
  EVENT_PENDING: "event-pending",
} as const satisfies Record<string, TPortfolioPriceDecisionState>;
export const PORTFOLIO_PRICE_DECISION_STATE_LABELS: Record<
  TPortfolioPriceDecisionState,
  Record<string, string>
> = {
  fresh: { "en-US": "Fresh price", "ru-RU": "Свежая цена" },
  stale: { "en-US": "Older price", "ru-RU": "Старая цена" },
  manual: { "en-US": "Manual estimate", "ru-RU": "Ручная оценка" },
  missing: { "en-US": "Price unavailable", "ru-RU": "Цена недоступна" },
  "identity-conflict": { "en-US": "Security identity conflict", "ru-RU": "Конфликт идентичности бумаги" },
  "event-pending": { "en-US": "Corporate event needs review", "ru-RU": "Требуется сверка корпоративного события" },
};
export const PORTFOLIO_PRICE_DECISION_STATE_VARIANTS: Record<
  TPortfolioPriceDecisionState,
  ChipVariant
> = {
  fresh: "emerald",
  stale: "outline",
  manual: "default",
  missing: "destructive",
  "identity-conflict": "destructive",
  "event-pending": "outline",
};

export const PORTFOLIO_PRICE_EVIDENCE_KIND = ["quote", "daily-close", "manual"] as const;
export type TPortfolioPriceEvidenceKind = (typeof PORTFOLIO_PRICE_EVIDENCE_KIND)[number];
export const PORTFOLIO_PRICE_EVIDENCE_KIND_OBJ = {
  QUOTE: "quote",
  DAILY_CLOSE: "daily-close",
  MANUAL: "manual",
} as const satisfies Record<string, TPortfolioPriceEvidenceKind>;
export const PORTFOLIO_PRICE_EVIDENCE_KIND_LABELS: Record<TPortfolioPriceEvidenceKind, Record<string, string>> = {
  quote: { "en-US": "Quote", "ru-RU": "Котировка" },
  "daily-close": { "en-US": "Daily close", "ru-RU": "Дневное закрытие" },
  manual: { "en-US": "Manual price", "ru-RU": "Ручная цена" },
};
export const PORTFOLIO_PRICE_EVIDENCE_KIND_VARIANTS: Record<TPortfolioPriceEvidenceKind, ChipVariant> = {
  quote: "emerald",
  "daily-close": "secondary",
  manual: "default",
};

export const PORTFOLIO_PRICE_BASIS = ["raw", "split-adjusted", "unknown"] as const;
export type TPortfolioPriceBasis = (typeof PORTFOLIO_PRICE_BASIS)[number];
export const PORTFOLIO_PRICE_BASIS_OBJ = {
  RAW: "raw",
  SPLIT_ADJUSTED: "split-adjusted",
  UNKNOWN: "unknown",
} as const satisfies Record<string, TPortfolioPriceBasis>;
export const PORTFOLIO_PRICE_BASIS_LABELS: Record<TPortfolioPriceBasis, Record<string, string>> = {
  raw: { "en-US": "Unadjusted", "ru-RU": "Без корректировки" },
  "split-adjusted": { "en-US": "Split-adjusted", "ru-RU": "С учётом дроблений" },
  unknown: { "en-US": "Unknown units", "ru-RU": "Единицы не подтверждены" },
};
export const PORTFOLIO_PRICE_BASIS_VARIANTS: Record<TPortfolioPriceBasis, ChipVariant> = {
  raw: "emerald",
  "split-adjusted": "outline",
  unknown: "destructive",
};

export const PORTFOLIO_PRICE_ACTION = [
  "retry-fmp",
  "inspect-identity",
  "confirm-identity",
  "set-manual-price",
  "return-to-fmp",
  "record-corporate-event",
] as const;
export type TPortfolioPriceAction = (typeof PORTFOLIO_PRICE_ACTION)[number];
export const PORTFOLIO_PRICE_ACTION_OBJ = {
  RETRY_FMP: "retry-fmp",
  INSPECT_IDENTITY: "inspect-identity",
  CONFIRM_IDENTITY: "confirm-identity",
  SET_MANUAL_PRICE: "set-manual-price",
  RETURN_TO_FMP: "return-to-fmp",
  RECORD_CORPORATE_EVENT: "record-corporate-event",
} as const satisfies Record<string, TPortfolioPriceAction>;
export const PORTFOLIO_PRICE_ACTION_LABELS: Record<TPortfolioPriceAction, Record<string, string>> = {
  "retry-fmp": { "en-US": "Check FMP", "ru-RU": "Проверить FMP" },
  "inspect-identity": { "en-US": "Review security match", "ru-RU": "Сверить выпуск бумаги" },
  "confirm-identity": { "en-US": "Confirm security match", "ru-RU": "Подтвердить выпуск бумаги" },
  "set-manual-price": { "en-US": "Enter a manual price", "ru-RU": "Указать цену вручную" },
  "return-to-fmp": { "en-US": "Return to FMP", "ru-RU": "Вернуть FMP" },
  "record-corporate-event": { "en-US": "Record a corporate event", "ru-RU": "Отразить корпоративное событие" },
};
export const PORTFOLIO_PRICE_ACTION_VARIANTS: Record<TPortfolioPriceAction, ChipVariant> = {
  "retry-fmp": "secondary",
  "inspect-identity": "outline",
  "confirm-identity": "default",
  "set-manual-price": "default",
  "return-to-fmp": "secondary",
  "record-corporate-event": "outline",
};

export const PORTFOLIO_FMP_ERROR_STATE = [
  "unavailable",
  "access-denied",
  "rate-limited",
  "server-error",
  "timeout",
  "incomplete-response",
  "not-found",
] as const;
export type TPortfolioFmpErrorState = (typeof PORTFOLIO_FMP_ERROR_STATE)[number];
export const PORTFOLIO_FMP_ERROR_STATE_OBJ = {
  UNAVAILABLE: "unavailable",
  ACCESS_DENIED: "access-denied",
  RATE_LIMITED: "rate-limited",
  SERVER_ERROR: "server-error",
  TIMEOUT: "timeout",
  INCOMPLETE_RESPONSE: "incomplete-response",
  NOT_FOUND: "not-found",
} as const satisfies Record<string, TPortfolioFmpErrorState>;
export const PORTFOLIO_FMP_ERROR_STATE_LABELS: Record<TPortfolioFmpErrorState, Record<string, string>> = {
  unavailable: { "en-US": "FMP unavailable", "ru-RU": "FMP недоступен" },
  "access-denied": { "en-US": "FMP access denied", "ru-RU": "FMP отказал в доступе" },
  "rate-limited": { "en-US": "FMP rate limit", "ru-RU": "Ограничение запросов FMP" },
  "server-error": { "en-US": "FMP server error", "ru-RU": "Ошибка сервера FMP" },
  timeout: { "en-US": "FMP request timed out", "ru-RU": "Истекло время ожидания FMP" },
  "incomplete-response": { "en-US": "Incomplete FMP response", "ru-RU": "Неполный ответ FMP" },
  "not-found": { "en-US": "No FMP data", "ru-RU": "Данные FMP не найдены" },
};
export const PORTFOLIO_FMP_ERROR_STATE_VARIANTS: Record<TPortfolioFmpErrorState, ChipVariant> = {
  unavailable: "destructive",
  "access-denied": "destructive",
  "rate-limited": "outline",
  "server-error": "destructive",
  timeout: "outline",
  "incomplete-response": "outline",
  "not-found": "secondary",
};

export const PORTFOLIO_PRICE_SYNC_STATUS = [
  "queued",
  "running",
  "succeeded",
  "partial",
  "failed",
] as const;
export type TPortfolioPriceSyncStatus = (typeof PORTFOLIO_PRICE_SYNC_STATUS)[number];
export const PORTFOLIO_PRICE_SYNC_STATUS_OBJ = {
  QUEUED: "queued",
  RUNNING: "running",
  SUCCEEDED: "succeeded",
  PARTIAL: "partial",
  FAILED: "failed",
} as const satisfies Record<string, TPortfolioPriceSyncStatus>;
export const PORTFOLIO_PRICE_SYNC_STATUS_LABELS: Record<TPortfolioPriceSyncStatus, Record<string, string>> = {
  queued: { "en-US": "Queued", "ru-RU": "В очереди" },
  running: { "en-US": "Checking FMP", "ru-RU": "Проверяем FMP" },
  succeeded: { "en-US": "Completed", "ru-RU": "Завершено" },
  partial: { "en-US": "Completed with unavailable data", "ru-RU": "Завершено частично" },
  failed: { "en-US": "Failed", "ru-RU": "Ошибка" },
};
export const PORTFOLIO_PRICE_SYNC_STATUS_VARIANTS: Record<TPortfolioPriceSyncStatus, ChipVariant> = {
  queued: "outline",
  running: "secondary",
  succeeded: "emerald",
  partial: "outline",
  failed: "destructive",
};

/** True while a portfolio price synchronization has not finished (waiting or running). */
export function isPortfolioPriceSyncActive(status: string): boolean {
  return status === PORTFOLIO_PRICE_SYNC_STATUS_OBJ.QUEUED || status === PORTFOLIO_PRICE_SYNC_STATUS_OBJ.RUNNING;
}

export const PORTFOLIO_MARKET_EVENT_KIND = [
  "rename", "split", "reverse-split", "conversion", "delisting", "inactivity", "reactivation", "cancellation",
] as const;
export type TPortfolioMarketEventKind = (typeof PORTFOLIO_MARKET_EVENT_KIND)[number];
export const PORTFOLIO_MARKET_EVENT_KIND_OBJ = {
  RENAME: "rename", SPLIT: "split", REVERSE_SPLIT: "reverse-split", CONVERSION: "conversion",
  DELISTING: "delisting", INACTIVITY: "inactivity", REACTIVATION: "reactivation", CANCELLATION: "cancellation",
} as const satisfies Record<string, TPortfolioMarketEventKind>;
export const PORTFOLIO_MARKET_EVENT_KIND_LABELS: Record<TPortfolioMarketEventKind, Record<string, string>> = {
  rename: { "en-US": "Ticker change", "ru-RU": "Смена тикера" },
  split: { "en-US": "Split", "ru-RU": "Дробление" },
  "reverse-split": { "en-US": "Reverse split", "ru-RU": "Обратное дробление" },
  conversion: { "en-US": "Conversion", "ru-RU": "Конвертация" },
  delisting: { "en-US": "Delisting", "ru-RU": "Делистинг" },
  inactivity: { "en-US": "Inactive according to FMP", "ru-RU": "Неактивна по данным FMP" },
  reactivation: { "en-US": "Relisting or reactivation", "ru-RU": "Возобновление торгов" },
  cancellation: { "en-US": "Cancellation", "ru-RU": "Аннулирование" },
};
export const PORTFOLIO_MARKET_EVENT_KIND_VARIANTS: Record<TPortfolioMarketEventKind, ChipVariant> = {
  rename: "secondary", split: "outline", "reverse-split": "outline", conversion: "outline",
  delisting: "destructive", inactivity: "outline", reactivation: "emerald", cancellation: "destructive",
};

export const PORTFOLIO_VALUATION_REVISION_REASON = [
  "automatic-price-update", "manual-price-update", "policy-recalculation", "corporate-event", "revision-restoration", "legacy-preservation", "income-settlement-correction", "broker-fee-history",
] as const;
export type TPortfolioValuationRevisionReason = (typeof PORTFOLIO_VALUATION_REVISION_REASON)[number];
export const PORTFOLIO_VALUATION_REVISION_REASON_OBJ = {
  AUTOMATIC_PRICE_UPDATE: "automatic-price-update",
  MANUAL_PRICE_UPDATE: "manual-price-update",
  POLICY_RECALCULATION: "policy-recalculation",
  CORPORATE_EVENT: "corporate-event",
  REVISION_RESTORATION: "revision-restoration",
  LEGACY_PRESERVATION: "legacy-preservation",
  INCOME_SETTLEMENT_CORRECTION: "income-settlement-correction",
  BROKER_FEE_HISTORY: "broker-fee-history",
} as const satisfies Record<string, TPortfolioValuationRevisionReason>;
export const PORTFOLIO_VALUATION_REVISION_REASON_LABELS: Record<TPortfolioValuationRevisionReason, Record<string, string>> = {
  "automatic-price-update": { "en-US": "Automatic price update", "ru-RU": "Автоматическое обновление цены" },
  "manual-price-update": { "en-US": "Manual price update", "ru-RU": "Ручное обновление цены" },
  "policy-recalculation": { "en-US": "Policy recalculation", "ru-RU": "Пересчёт по правилам оценки" },
  "corporate-event": { "en-US": "Confirmed corporate event", "ru-RU": "Подтверждённое корпоративное событие" },
  "revision-restoration": { "en-US": "Restored snapshot revision", "ru-RU": "Восстановление версии оценки" },
  "legacy-preservation": { "en-US": "Preserved legacy snapshot", "ru-RU": "Сохранённая старая оценка" },
  "income-settlement-correction": { "en-US": "Confirmed dividend settlement", "ru-RU": "Подтверждённое уточнение дивидендов" },
  "broker-fee-history": { "en-US": "Confirmed broker fee history", "ru-RU": "Подтверждённые исторические комиссии брокера" },
};
export const PORTFOLIO_VALUATION_REVISION_REASON_VARIANTS: Record<TPortfolioValuationRevisionReason, ChipVariant> = {
  "automatic-price-update": "secondary", "manual-price-update": "default", "policy-recalculation": "outline", "corporate-event": "secondary", "revision-restoration": "default", "legacy-preservation": "outline", "income-settlement-correction": "secondary", "broker-fee-history": "secondary",
};

export const PORTFOLIO_CORPORATE_CORRECTION_KIND = [
  "split", "reverse-split", "conversion", "write-off", "cash-sale",
] as const;
export type TPortfolioCorporateCorrectionKind = (typeof PORTFOLIO_CORPORATE_CORRECTION_KIND)[number];
export const PORTFOLIO_CORPORATE_CORRECTION_KIND_OBJ = {
  SPLIT: "split", REVERSE_SPLIT: "reverse-split", CONVERSION: "conversion", WRITE_OFF: "write-off", CASH_SALE: "cash-sale",
} as const satisfies Record<string, TPortfolioCorporateCorrectionKind>;
export const PORTFOLIO_CORPORATE_CORRECTION_KIND_LABELS: Record<TPortfolioCorporateCorrectionKind, Record<string, string>> = {
  split: { "en-US": "Split", "ru-RU": "Дробление" },
  "reverse-split": { "en-US": "Reverse split", "ru-RU": "Обратное дробление" },
  conversion: { "en-US": "Conversion", "ru-RU": "Конвертация" },
  "write-off": { "en-US": "Write-off", "ru-RU": "Списание" },
  "cash-sale": { "en-US": "Cash sale", "ru-RU": "Выплата за акции" },
};
export const PORTFOLIO_CORPORATE_CORRECTION_KIND_VARIANTS: Record<TPortfolioCorporateCorrectionKind, ChipVariant> = {
  split: "outline", "reverse-split": "outline", conversion: "outline", "write-off": "destructive", "cash-sale": "default",
};

export const PORTFOLIO_CONFIRMED_MARKET_EVENT_OPERATION_KIND = [
  "split", "conversion", "write-off", "cash-sale",
] as const;
export type TPortfolioConfirmedMarketEventOperationKind = (typeof PORTFOLIO_CONFIRMED_MARKET_EVENT_OPERATION_KIND)[number];
export const PORTFOLIO_CONFIRMED_MARKET_EVENT_OPERATION_KIND_OBJ = {
  SPLIT: "split", CONVERSION: "conversion", WRITE_OFF: "write-off", CASH_SALE: "cash-sale",
} as const satisfies Record<string, TPortfolioConfirmedMarketEventOperationKind>;
export const PORTFOLIO_CONFIRMED_SHARE_EVENT_KIND = ["split", "conversion"] as const;
export const PORTFOLIO_CONFIRMED_MARKET_EVENT_OPERATION_KIND_LABELS: Record<TPortfolioConfirmedMarketEventOperationKind, Record<string, string>> = {
  split: { "en-US": "Split or reverse split", "ru-RU": "Дробление или обратное дробление" },
  conversion: { "en-US": "Share conversion", "ru-RU": "Конвертация акций" },
  "write-off": { "en-US": "Write-off", "ru-RU": "Списание" },
  "cash-sale": { "en-US": "Cash sale", "ru-RU": "Выплата за акции" },
};
export const PORTFOLIO_CONFIRMED_MARKET_EVENT_OPERATION_KIND_VARIANTS: Record<TPortfolioConfirmedMarketEventOperationKind, ChipVariant> = {
  split: "outline", conversion: "outline", "write-off": "destructive", "cash-sale": "default",
};

export const PORTFOLIO_BROKER_EVENT_EVIDENCE_KIND = [
  "broker-statement", "broker-confirmation", "broker-transaction",
] as const;
export type TPortfolioBrokerEventEvidenceKind = (typeof PORTFOLIO_BROKER_EVENT_EVIDENCE_KIND)[number];
export const PORTFOLIO_BROKER_EVENT_EVIDENCE_KIND_OBJ = {
  STATEMENT: "broker-statement", CONFIRMATION: "broker-confirmation", TRANSACTION: "broker-transaction",
} as const satisfies Record<string, TPortfolioBrokerEventEvidenceKind>;
export const PORTFOLIO_BROKER_EVENT_EVIDENCE_KIND_LABELS: Record<TPortfolioBrokerEventEvidenceKind, Record<string, string>> = {
  "broker-statement": { "en-US": "Broker statement", "ru-RU": "Выписка брокера" },
  "broker-confirmation": { "en-US": "Broker confirmation", "ru-RU": "Подтверждение брокера" },
  "broker-transaction": { "en-US": "Broker transaction", "ru-RU": "Сделка у брокера" },
};
export const PORTFOLIO_BROKER_EVENT_EVIDENCE_KIND_VARIANTS: Record<TPortfolioBrokerEventEvidenceKind, ChipVariant> = {
  "broker-statement": "secondary", "broker-confirmation": "outline", "broker-transaction": "default",
};

export const PORTFOLIO_BLOCKED_DATA_COMPLETENESS = [
  "complete",
  "partial",
  "unknown",
] as const;
export type TPortfolioBlockedDataCompleteness =
  (typeof PORTFOLIO_BLOCKED_DATA_COMPLETENESS)[number];
export const PORTFOLIO_BLOCKED_DATA_COMPLETENESS_OBJ = {
  COMPLETE: "complete",
  PARTIAL: "partial",
  UNKNOWN: "unknown",
} as const satisfies Record<string, TPortfolioBlockedDataCompleteness>;
export const PORTFOLIO_POSITION_AVAILABILITY_FILTER = ["all", "blocked", "available"] as const;
export type TPortfolioPositionAvailabilityFilter =
  (typeof PORTFOLIO_POSITION_AVAILABILITY_FILTER)[number];
export const PORTFOLIO_POSITION_AVAILABILITY_FILTER_OBJ = {
  ALL: "all",
  BLOCKED: "blocked",
  AVAILABLE: "available",
} as const satisfies Record<string, TPortfolioPositionAvailabilityFilter>;

export const POSITION_HISTORY_CONVERSION_STATUS = ["completed", "pending"] as const;
export const POSITION_HISTORY_EVENT_TYPE = ["transaction", "conversion", "security_transfer", "quantity_correction", "option_event"] as const;
export type TPositionHistoryEventType = (typeof POSITION_HISTORY_EVENT_TYPE)[number];
export const POSITION_HISTORY_EVENT_TYPE_OBJ = {
  TRANSACTION: "transaction", CONVERSION: "conversion", SECURITY_TRANSFER: "security_transfer", QUANTITY_CORRECTION: "quantity_correction", OPTION_EVENT: "option_event",
} as const satisfies Record<string, TPositionHistoryEventType>;
export const POSITION_HISTORY_EVENT_TYPE_LABELS: Record<TPositionHistoryEventType, Record<string, string>> = {
  transaction: { "en-US": "Trade", "ru-RU": "Сделка" },
  conversion: { "en-US": "Conversion", "ru-RU": "Конвертация" },
  security_transfer: { "en-US": "Security transfer", "ru-RU": "Перенос бумаг" },
  quantity_correction: { "en-US": "Quantity correction", "ru-RU": "Корректировка количества" },
  option_event: { "en-US": "Option lifecycle", "ru-RU": "Событие опциона" },
};
export const POSITION_HISTORY_EVENT_TYPE_VARIANTS: Record<TPositionHistoryEventType, ChipVariant> = {
  transaction: "outline", conversion: "secondary", security_transfer: "secondary", quantity_correction: "secondary", option_event: "secondary",
};
export const POSITION_HISTORY_CORRECTION_EVENT_TYPES = [
  POSITION_HISTORY_EVENT_TYPE_OBJ.SECURITY_TRANSFER, POSITION_HISTORY_EVENT_TYPE_OBJ.QUANTITY_CORRECTION,
] as const;
export type TPositionHistoryConversionStatus =
  (typeof POSITION_HISTORY_CONVERSION_STATUS)[number];
export const POSITION_HISTORY_CONVERSION_STATUS_OBJ = {
  COMPLETED: "completed",
  PENDING: "pending",
} as const satisfies Record<string, TPositionHistoryConversionStatus>;
export const PORTFOLIO_BLOCKED_DATA_COMPLETENESS_LABELS: Record<
  TPortfolioBlockedDataCompleteness,
  Record<string, string>
> = {
  complete: { "en-US": "Complete", "ru-RU": "Полная" },
  partial: { "en-US": "Partial", "ru-RU": "Частичная" },
  unknown: { "en-US": "Unknown", "ru-RU": "Неизвестна" },
};
export const PORTFOLIO_BLOCKED_DATA_COMPLETENESS_VARIANTS: Record<
  TPortfolioBlockedDataCompleteness,
  ChipVariant
> = {
  complete: "emerald",
  partial: "outline",
  unknown: "destructive",
};

export const PORTFOLIO_VALUATION_REASON = [
  "missing_market_price",
  "pending_conversion",
  "missing_fx_rate",
] as const;
export type TPortfolioValuationReason =
  (typeof PORTFOLIO_VALUATION_REASON)[number];
export const PORTFOLIO_VALUATION_REASON_OBJ = {
  MISSING_MARKET_PRICE: "missing_market_price",
  PENDING_CONVERSION: "pending_conversion",
  MISSING_FX_RATE: "missing_fx_rate",
} as const satisfies Record<string, TPortfolioValuationReason>;
export const PORTFOLIO_VALUATION_REASON_LABELS: Record<
  TPortfolioValuationReason,
  Record<string, string>
> = {
  missing_market_price: { "en-US": "Missing market price", "ru-RU": "Нет рыночной цены" },
  pending_conversion: { "en-US": "Pending conversion", "ru-RU": "Ожидается конвертация" },
  missing_fx_rate: { "en-US": "Missing exchange rate", "ru-RU": "Нет курса валюты" },
};
export const PORTFOLIO_VALUATION_REASON_VARIANTS: Record<
  TPortfolioValuationReason,
  ChipVariant
> = {
  missing_market_price: "destructive",
  pending_conversion: "secondary",
  missing_fx_rate: "destructive",
};

export const PORTFOLIO_PARTIAL_METRIC_UNAVAILABLE_REASON = [
  "value_unavailable",
  "coverage_unknown",
  "coverage_changed",
] as const;
export type TPortfolioPartialMetricUnavailableReason =
  (typeof PORTFOLIO_PARTIAL_METRIC_UNAVAILABLE_REASON)[number];
export const PORTFOLIO_PARTIAL_METRIC_UNAVAILABLE_REASON_OBJ = {
  VALUE_UNAVAILABLE: "value_unavailable",
  COVERAGE_UNKNOWN: "coverage_unknown",
  COVERAGE_CHANGED: "coverage_changed",
} as const satisfies Record<string, TPortfolioPartialMetricUnavailableReason>;

// ── portfolioAccountClosureViolationKind ────────────────────────────────────
export const PORTFOLIO_ACCOUNT_CLOSURE_VIOLATION_KIND = ["late-operations", "cash", "position", "unverifiable"] as const;
export type TPortfolioAccountClosureViolationKind = (typeof PORTFOLIO_ACCOUNT_CLOSURE_VIOLATION_KIND)[number];
export const PORTFOLIO_ACCOUNT_CLOSURE_VIOLATION_KIND_OBJ = {
  LATE_OPERATIONS: "late-operations",
  CASH: "cash",
  POSITION: "position",
  UNVERIFIABLE: "unverifiable",
} as const satisfies Record<string, TPortfolioAccountClosureViolationKind>;

// ── portfolioEventPlanStatus ────────────────────────────────────────────────
// How a planned broker-confirmed event relates to what the ledger already holds.
export const PORTFOLIO_EVENT_PLAN_STATUS = ["create", "unchanged", "conflict"] as const;
export type TPortfolioEventPlanStatus = (typeof PORTFOLIO_EVENT_PLAN_STATUS)[number];
export const PORTFOLIO_EVENT_PLAN_STATUS_OBJ = {
  CREATE: "create",
  UNCHANGED: "unchanged",
  CONFLICT: "conflict",
} as const satisfies Record<string, TPortfolioEventPlanStatus>;

// ── portfolioPriceJumpCheck ─────────────────────────────────────────────────
// Outcome of re-checking a suspicious price jump against provider history.
export const PORTFOLIO_PRICE_JUMP_CHECK = ["confirmed", "conflicting", "unavailable"] as const;
export type TPortfolioPriceJumpCheck = (typeof PORTFOLIO_PRICE_JUMP_CHECK)[number];
export const PORTFOLIO_PRICE_JUMP_CHECK_OBJ = {
  CONFIRMED: "confirmed",
  CONFLICTING: "conflicting",
  UNAVAILABLE: "unavailable",
} as const satisfies Record<string, TPortfolioPriceJumpCheck>;
